import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import {
  JOB_STATUS_CONFIG,
  JOB_STATUS_ORDER,
} from "./StatusControl/configs/jobConfig";

export interface JobStatusTimelineProps {
  currentStatus: string;
  statusChangedAt?: string;
}

const GRAY = "#d1d5db";
const FUTURE_OUTLINE = "#e5e7eb";
const FUTURE_LABEL_COLOR = "#9ca3af";

function getShortLabels(t: (key: string) => string): Record<string, string> {
  return {
    role_in_preparation: t("recruiting:statusTimeline.short.prep"),
    find_candidates_passive: t("recruiting:statusTimeline.short.passive"),
    find_candidates_active: t("recruiting:statusTimeline.short.active"),
    recruitment_in_progress: t("recruiting:statusTimeline.short.recruiting"),
    on_hold: t("recruiting:statusTimeline.short.hold"),
    delivered: t("recruiting:statusTimeline.short.delivered"),
    archived: t("recruiting:statusTimeline.short.archived"),
  };
}

function buildTooltip(
  statusKey: string,
  label: string,
  currentStatus: string,
  statusChangedAt: string | undefined,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  const isCurrent = statusKey === currentStatus;
  const currentIdx = JOB_STATUS_ORDER.indexOf(currentStatus);
  const statusIdx = JOB_STATUS_ORDER.indexOf(statusKey);
  const isPast = statusIdx < currentIdx;

  if (isPast) {
    return `\u2713 ${label} -- ${t("recruiting:statusTimeline.completed")}`;
  }
  if (isCurrent && statusChangedAt) {
    return `\u25CF ${label} -- ${t("recruiting:statusTimeline.currentSince", { date: new Date(statusChangedAt).toLocaleDateString() })}`;
  }
  if (isCurrent) {
    return `\u25CF ${label} -- ${t("recruiting:statusTimeline.current")}`;
  }
  if (statusIdx === currentIdx + 1) {
    return `\u25CB ${label} -- ${t("recruiting:statusTimeline.nextStep")}`;
  }
  return `\u25CB ${label}`;
}

const JobStatusTimeline: React.FC<JobStatusTimelineProps> = ({
  currentStatus,
  statusChangedAt,
}) => {
  const { t } = useTranslation(["recruiting"]);
  const dotSize = 14;
  const activeDotSize = 18;
  const connH = 2;

  const currentIdx = JOB_STATUS_ORDER.indexOf(currentStatus);
  const shortLabels = getShortLabels(t);

  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "nowrap",
          minWidth: "fit-content",
          gap: 0,
        }}
      >
        {JOB_STATUS_ORDER.map((statusKey, slotIdx) => {
          const config = JOB_STATUS_CONFIG[statusKey] ?? {
            label: statusKey,
            color: GRAY,
            bgColor: "#fff",
          };
          const isActive = statusKey === currentStatus;
          const isPast = slotIdx < currentIdx;
          const isLast = slotIdx === JOB_STATUS_ORDER.length - 1;

          const size = isActive ? activeDotSize : dotSize;
          const bgColor = isActive || isPast ? config.color : "transparent";
          const borderColor =
            isActive || isPast ? config.color : FUTURE_OUTLINE;
          const labelColor =
            isActive || isPast ? config.color : FUTURE_LABEL_COLOR;
          const labelWeight = isActive ? 600 : 400;
          const shortLabel = shortLabels[statusKey] || config.label;

          const tooltip = buildTooltip(
            statusKey,
            config.label,
            currentStatus,
            statusChangedAt,
            t,
          );

          const connColor = isPast ? config.color : FUTURE_OUTLINE;

          return (
            <React.Fragment key={statusKey}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Tooltip title={tooltip} arrow placement="top">
                  <div
                    data-testid={`job-status-dot-${statusKey}`}
                    style={{
                      width: size,
                      height: size,
                      borderRadius: "50%",
                      backgroundColor: bgColor,
                      border: `2px solid ${borderColor}`,
                      boxShadow: isActive
                        ? `0 0 0 3px ${config.color}33`
                        : undefined,
                      flexShrink: 0,
                      cursor: "default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {isPast && (
                      <span
                        style={{
                          color: "white",
                          fontSize: "8px",
                          lineHeight: 1,
                          fontWeight: 700,
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                </Tooltip>
                <span
                  style={{
                    marginTop: 3,
                    fontSize: "9px",
                    color: labelColor,
                    fontWeight: labelWeight,
                    maxWidth: 56,
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortLabel}
                </span>
              </div>

              {!isLast && (
                <div
                  style={{
                    flex: 1,
                    minWidth: 8,
                    height: connH,
                    backgroundColor: connColor,
                    marginTop:
                      (isActive ? activeDotSize : dotSize) / 2 - connH / 2,
                    flexShrink: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default JobStatusTimeline;
